import { Sale } from "@sales/data/sales/SaleData";
import { SaleOverviewPage } from "./pages/SaleOverviewPage";
import SaleManageBiddersPage from "../bidders/SaleManageBiddersPage";
import SaleLotOverviewPage from "../lots/SaleLotOverviewPage";
import SaleInvoicingOverviewPage from "../invoicing/pages/SaleInvoicingOverviewPage";
import SalePlanningTasksPage from "../planningTasks/pages/SalePlanningTasksPage";
import SaleSandboxPage from "./pages/SaleSandboxPage";
import { SaleQuickActionsButton } from "./components/buttons/SalesAppQuickActions";
import AgTechMultiPageTabPage from "@agtech/web/components/Pages/Tabbed/Flavors/AgTechMultiPageTabPage";
import AgTechActionPane from "@agtech/web/components/Headers/AgTechActionPane";
import { AgTechWebAppHeaderButton } from "@agtech/web/components/App/Templates/App/Layout/AgTechWebAppHeader";
import AgTechTabTourButton from "@agtech/web/components/App/Tour/AgTechTabTourButton";
import SaleDialog from "./dialogs/SaleDialog";
import { useCurrentSale } from "@sales/data/sales/SaleDataStore";
import { useSaleDataChangeSelectedSaleAction } from "@sales/data/sales/SaleDataActions";
import AgTechHeaderUserDetail from "@agtech/web/integrations/users/widgets/AgTechHeaderUserDetail";
import SalePaymentModal from "./modals/SalePaymentModal";
import SaleReportsPage from "./pages/SaleReportsPage";
import AgTechNoRecordsPage from "@agtech/web/components/Pages/Error/AgTechNoRecordsPage";
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface";
import { SaleDataIsSaleLicensedQuery } from "@sales/data/sales/SaleDataQueries";
import { useAgTechQuery } from "@agtech/core/data/queries/AgTechDataQueries";
import { SaleOverviewPageTourConfig } from "./pages/SaleOverviewPageTourConfig";
import { SaleManageBiddersPageTourConfig } from "../bidders/SaleManageBiddersPageTourConfig";
import { SaleLotOverviewPageTourConfig } from "../lots/SaleLotOverviewPageTourConfig";
import { SaleReportsPageTourConfig } from "./pages/SaleReportsPageTourConfig";
import { SalePlanningTasksPageTourConfig } from "../planningTasks/pages/SalePlanningTasksPageTourConfig";

const SaleWorkspace = (props: { sale: Sale }) => {
    const currentSale = useCurrentSale();
    const saleChangeAction = useSaleDataChangeSelectedSaleAction();

    const currentSaleLicenseQuery = useAgTechQuery({
        query: SaleDataIsSaleLicensedQuery,
        queryContext: { saleId: currentSale.id }
    });

    return (
        <AgTechQueryLoadingSurface
            query={currentSaleLicenseQuery}
            content={{
                loadingText: 'Loading sale...',
                failureText: 'Failed to load sale'
            }}
        >
            {(isSaleLicensed) => {
                const paneContent = () => (
                    <div className="flex-col tall flex">
                        <AgTechActionPane
                            classes="ml-auto !h-[40px] flex"
                            elements={[
                                <div id='sale-header-create-button'>
                                    <AgTechWebAppHeaderButton
                                        icon='bi bi-plus-lg'
                                        styles={{ fontSize: 32 }}
                                        action={e => SaleDialog.Open(e, {})}
                                        tooltip='Create a new sale'
                                    />
                                </div>,
                                <div id='sale-header-refresh-button'>
                                    <AgTechWebAppHeaderButton
                                        icon='bi bi-arrow-clockwise'
                                        styles={{ fontSize: 24 }}
                                        action={async e => {
                                            await e.actions.executeDataActionWithVisual(
                                                saleChangeAction,
                                                { submittedEntity: currentSale.id },
                                                { message: 'Refreshing sale data...', delay: 1000 }
                                            )
                                        }}
                                        tooltip='Refresh current sale data'
                                    />
                                </div>,
                                <AgTechHeaderUserDetail profile={{ route: 'Profile' }} />,
                                <AgTechWebAppHeaderButton
                                    icon={'bi bi-grid-3x3-gap-fill'}
                                />
                            ]}
                        />
                        {isSaleLicensed && (
                            <div className="mt-auto ml-auto mb-[3px] column-based items-center flex">
                                <AgTechTabTourButton
                                    tourConfigs={{
                                        'sale-workspace-tab-overview': SaleOverviewPageTourConfig,
                                        'sale-workspace-tab-planning-tasks': SalePlanningTasksPageTourConfig,
                                        'sale-workspace-tab-manage-bidders': SaleManageBiddersPageTourConfig,
                                        'sale-workspace-tab-sell-lots': SaleLotOverviewPageTourConfig,
                                        'sale-workspace-tab-reports': SaleReportsPageTourConfig,
                                    }}
                                    classes='mr-3'
                                />
                                <div id='sale-quick-actions-button'>
                                    <SaleQuickActionsButton />
                                </div>
                            </div>
                        )}
                    </div>
                );
            
                const tabs = isSaleLicensed ? [
                    {
                        id: 'sale-workspace-tab-overview',
                        text: 'Overview',
                        content: () => true ? <SaleOverviewPage /> : <SaleSandboxPage />
                    },
                    {
                        id: 'sale-workspace-tab-planning-tasks',
                        text: 'Planning Tasks',
                        content: () => <SalePlanningTasksPage />
                    },
                    {
                        id: 'sale-workspace-tab-manage-bidders',
                        text: 'Manage Bidders',
                        content: () => <SaleManageBiddersPage />
                    },
                    {
                        id: 'sale-workspace-tab-sell-lots',
                        text: 'Sell Lots',
                        content: () => <SaleLotOverviewPage />
                    },
                    {
                        id: 'sale-workspace-tab-perform-invoicing',
                        text: 'Perform Invoicing',
                        content: () => <SaleInvoicingOverviewPage />
                    },
                    {
                        id: 'sale-workspace-tab-reports',
                        text: 'Reports',
                        content: () => <SaleReportsPage />
                    }
                ] : [
                    {
                        text: 'Overview',
                        content: () => (
                            <AgTechNoRecordsPage
                                message="This sale is not licensed for use."
                                messageClasses="text-xl"
                                promptClasses="text-more"
                                action={{
                                    prompt: 'To license this sale, click the button below',
                                    button: () => (
                                        <SalePaymentModal.Button
                                            classes="button-highlight py-2 px-4" 
                                            sale={currentSale}
                                            text="License Sale"
                                        />
                                    )
                                }}
                            />
                        )
                    }
                ];

                return (
                    <div className="stretched flex-col flex">
                        <AgTechMultiPageTabPage
                            title={props.sale.saleName}
                            height={120}
                            paneContent={paneContent}
                            tabs={tabs}
                            classes={{
                                header: '!border-0 !border-b rounded-none',
                                tabContainer: 'mb-[10px]',
                                tabs: {
                                    container: 'mb-2',
                                    tab: 'mr-4 px-4 py-[6px] border-none rounded font-semibold centered w-min-100 flex',
                                    inactive: 'text-secondary'
                                }
                            }}
                        />
                    </div>
                );
            }}
        </AgTechQueryLoadingSurface>
    )
}

export default SaleWorkspace